import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { QueryPagination, createQueryOptions } from '../../query';
import { useStoreApi } from '../api';
import { storeKeys } from '../query-key-factory';
import { StoreItemResponse } from '../types';
import { EventItemsResponse, EventStoreItem } from '../types/event';
import { EventIncludeOptions, EventSearch } from './search.types';

const useListEvents = <T = EventItemsResponse>({
  causeId,
  pagination,
  queryOptions,
  select,
}: {
  causeId?: string;
  pagination?: QueryPagination;
  queryOptions?: EventSearch;
  select?: (data: EventItemsResponse) => T;
}) => {
  const StoreApi = useStoreApi();
  const queryParams = new URLSearchParams();

  if (causeId) {
    queryParams.append('cause_id', causeId);
  }

  const getEvents = async (context: { meta?: { queryString?: string } }) => {
    const { queryString } = context.meta || {};
    const { data } = await StoreApi.get<EventItemsResponse>(`/store_items/events?${queryString}`);
    return data;
  };

  const { queryString, queryObject, queryPagination } = createQueryOptions<EventSearch>({
    search: queryOptions,
    pagination,
    existingSearchString: queryParams.toString(),
  });

  const key = causeId ? storeKeys.id(causeId).items.events.all : [];

  return useQuery({
    queryFn: getEvents,
    queryKey: [...key, queryObject, queryPagination],
    meta: { queryString },
    enabled: !!causeId,
    select,
    placeholderData: keepPreviousData,
  });
};

const useShowEvent = ({
  causeId,
  eventId,
  enabled,
  include = 'event_dates',
}: {
  causeId?: string;
  eventId?: string;
  enabled?: boolean;
  include?: EventIncludeOptions;
}) => {
  const StoreApi = useStoreApi();

  const queryParams = new URLSearchParams();
  queryParams.append('cause_id', causeId!);
  if (include) {
    queryParams.append('include', include);
  }

  const getEvent = async () => {
    const { data } = await StoreApi.get<StoreItemResponse<EventStoreItem>>(
      `/store_items/events/${eventId}?${queryParams.toString()}`
    );

    return data.store_item;
  };

  const key = causeId && eventId ? storeKeys.id(causeId).items.events.id(eventId).detail : [];

  return useQuery({
    queryKey: [...key, queryParams.toString()],
    queryFn: getEvent,
    enabled: !!causeId && !!eventId && enabled,
  });
};

export { useListEvents, useShowEvent };
